import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';
import { BuiltLogoMark } from '../ui/BuiltLogoMark';
import apiService from '../../services/ApiService';
import HeroBuildMosaic from './HeroBuildMosaic';
import type { HomeVehicleCard } from './homeTypes';

interface PublicHomeHeroProps {
  builds: HomeVehicleCard[];
  featuredVehicleId?: number | null;
}

/**
 * Pink Slips–influenced public hero: live builds scroll behind a heavy screen,
 * with Bilt brand copy and CTAs locked in the foreground.
 */
export const PublicHomeHero: React.FC<PublicHomeHeroProps> = ({
  builds,
  featuredVehicleId = null,
}) => {
  const navigate = useNavigate();

  const trackCta = (cta: string) => {
    void apiService.trackEvent('home_cta_clicked', { metadata: { cta, surface: 'hero' } });
  };

  return (
    <header className="relative min-h-[100dvh] overflow-hidden bg-[#0a0a0a]">
      <HeroBuildMosaic vehicles={builds} featuredVehicleId={featuredVehicleId} />

      {/* Screened back: mosaic stays visible; left/bottom hold copy contrast */}
      <div
        className="absolute inset-0 z-[1] bg-[#0a0a0a]/30 sm:bg-[#0a0a0a]/22"
        aria-hidden
      />
      <div
        className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/78 to-transparent"
        aria-hidden
      />
      <div
        className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent"
        aria-hidden
      />
      <div
        className="absolute inset-0 z-[1] bg-[radial-gradient(ellipse_at_18%_45%,rgba(249,115,22,0.10),transparent_50%)]"
        aria-hidden
      />
      <div className="film-grain z-[2] opacity-35" aria-hidden />

      <div className="relative z-[3] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end min-h-[100dvh] pb-16 sm:pb-24 pt-28">
        <div className="max-w-[40rem]">
          <div className="flex items-center gap-3 mb-8 sm:mb-10">
            <BuiltLogoMark size="md" />
            <span className="font-display text-2xl tracking-tight text-white">Bilt</span>
            <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-orange-300/90 bg-orange-500/10 border border-orange-500/25">
              Beta
            </span>
          </div>

          <p className="kicker mb-4 sm:mb-5">Your build deserves more than a caption</p>

          <h1 className="font-display text-[2.6rem] leading-[1.02] sm:text-5xl md:text-[4.25rem] font-semibold tracking-[-0.03em] text-white mb-5 sm:mb-6 text-balance">
            Every car{' '}
            <span className="italic font-medium text-orange-400 leading-[1.12] pb-1 inline-block">
              has a story.
            </span>
          </h1>

          <p className="text-gray-300/95 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10 max-w-[38ch]">
            Document modifications, media, events, and awards in one place that travels with the car.
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <Button
              variant="primary"
              size="lg"
              className="sm:px-9 shadow-brand"
              onClick={() => {
                trackCta('create_garage');
                navigate('/register');
              }}
            >
              Start your garage
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="sm:px-7"
              onClick={() => {
                trackCta('discover_builds');
                navigate('/discover');
              }}
            >
              Browse builds
            </Button>
          </div>

          <p className="mt-5 text-xs sm:text-sm text-gray-500">
            Free to start. Add a build in minutes. No credit card.
          </p>
        </div>
      </div>
    </header>
  );
};

export default PublicHomeHero;
